import React, { useEffect, useState } from 'react'
import Footer from '../Components/Footer/Footer'
import Header from '../Components/Header/Header'
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Divider } from 'antd';
import './TripDetails.css';
import { UserOutlined } from '@ant-design/icons';

/* =========================
   Status Configuration
========================= */
const STATUS_MAP = {
  1: { color: 'yellow', i18nKey: 'status.pending' },
  2: { color: 'blue', i18nKey: 'status.confirmed' },
  3: { color: 'red', i18nKey: 'status.cancelled' },
  4: { color: 'green', i18nKey: 'status.completed' },
  5: { color: 'orange', i18nKey: 'status.noAnswer' }
};

function TripDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [tripData, setTripData] = useState(null);

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('profile');

    if (!isLoggedIn) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    if (location.state?.tripData) {
      setTripData(location.state.tripData);
    } else {
      navigate('/my-trip');
    }
  }, [navigate, location, id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  };

  if (!tripData) {
    return (
      <>
        <Header />
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <p>{t('common.loading')}</p>
        </div>
        <Footer />
      </>
    );
  }

  const statusConfig = STATUS_MAP[tripData?.PrebookingAdvanceStatusId];

  return (
    <>
      <Header />

      <section
        className="banners"
        style={{ backgroundImage: `url(${'../../images/banners_bg.webp'})` }}
      >
        <div className="container">
          <div className="banner_head">
            <h1>{t('header.tripDetails')}</h1>
            <p>{t('trip.viewDetails')}</p>
          </div>

          <div className="bredcrub">
            <button onClick={() => navigate('/')}>{t('header.home')}</button>
            <span> / </span>
            <button onClick={() => navigate('/my-trip')}>{t('header.myTrips')}</button>
            <span> / </span>
            <p>{t('header.tripDetails')}</p>
          </div>
        </div>
      </section>

      <section className="trip_details_section ptb100">
        <div className="container">

          <div className="trip_details_header">
            <button className="back_button" onClick={() => navigate('/my-trip')}>
              ← {t('common.back')}
            </button>
          </div>

          <div className="trip_details_content">

            {/* ===== Quick Info ===== */}
            <div className="info_section">

              <div className="info_card">
                <div className="info_card_title">{t('trip.status')}</div>
                <Tag
                  color={statusConfig?.color || 'default'}
                  className="status_tag"
                >
                  {statusConfig
                    ? t(statusConfig.i18nKey)
                    : t('status.unknown')}
                </Tag>
              </div>

              <div className="info_card">
                <div className="info_card_title">{t('trip.totalCost')}</div>
                <p className="info_card_value highlight">
                  {tripData?.RealCost
                    ? `${tripData.RealCost} SAR`
                    : t('trip.notCalculated')}
                </p>
              </div>

              <div className="info_card">
                <div className="info_card_title">{t('trip.passengers')}</div>
                <p className="info_card_value">
                  {tripData?.PassengerCount || 0}
                </p>
              </div>

              <div className="info_card">
                <div className="info_card_title">{t('trip.paymentType')}</div>
                <p className="info_card_value">
                  {tripData?.PaymentType
                    ? t('payment.cash')
                    : t('payment.online')}
                </p>
              </div>

            </div>

            {/* ===== Details Card ===== */}
            <Card bordered={false}>
              <h2>📍 {t('trip.information')}</h2>
              <Divider />

              <Descriptions bordered column={2}>
                <Descriptions.Item label={t('trip.sourceCity')}>
                  {tripData?.SourceCity || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.destinationCity')}>
                  {tripData?.DestinationCity || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.startDate')}>
                  {formatDate(tripData?.StartDate)}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.startTime')}>
                  {tripData?.StartTime || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.creationDate')}>
                  {formatDate(tripData?.CreationDate)}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.realCost')}>
                  {tripData?.RealCost
                    ? `${tripData.RealCost} SAR`
                    : t('trip.notCalculated')}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.passengerName')}>
                  {tripData?.PassengerName || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.passengerPhone')}>
                  {tripData?.PassengerPhone || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.passengerCount')}>
                  {tripData?.PassengerCount || 0}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.bagCount')}>
                  {tripData?.BagCount || 0}
                </Descriptions.Item>

                <Descriptions.Item label={<><UserOutlined /> {t('trip.employeeName')}</>}>
                  {tripData?.EmployeeName || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={t('trip.description')} span={2}>
                  {tripData?.Description || t('trip.noDescription')}
                </Descriptions.Item>
              </Descriptions>
            </Card>

          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default TripDetails;
